import { Injectable } from "@angular/core";
import { Router } from "@angular/router";

import { CommunitiesService, NotificationService, ParticipationGate, ProductRequirement, ProductService, ReactionType, UserService, computeParticipationGate, environment } from "@mypxplat/xplat/core";

import { AppService } from "./app.service";
import { AvoService } from "./avo.service";
import { WebCommunityService } from "./community.service";

/** What `persist` needs to write a reaction and tell the right people about it. */
export interface CommunityReactionPersistArgs {
  /** The post or reply being reacted to. */
  post: any;
  selectedType: ReactionType;
  previousTypes: ReactionType[];
  /** The final desired set — the UI has already toggled optimistically. */
  nextTypes: ReactionType[];
  communityStringId?: string;
  betaStringId?: string;
  /**
   * The top-level post, when `post` is a reply. Lets the notification link to the parent
   * thread rather than the reply doc. Omit for a reaction on a parent post.
   */
  originalPost?: any;
  /** Channel names for the Avo payload; the feed formats these differently per surface. */
  channelName?: string;
}

/**
 * The one place a community reaction is authorised, written and announced.
 *
 * Extracted from `comment.component` when the list view gained its own reaction control
 * (both surfaces must send the same DENG-845 notification, hit the same participation gate
 * and emit the same Avo event — two copies would drift the first time one changed).
 *
 * Gate state is computed on demand rather than subscribed to: callers that need it for
 * template state keep their own reactive copy, but a click handler can just ask.
 */
@Injectable({ providedIn: "root" })
export class CommunityReactionService {
  constructor(
    private _router: Router,
    private _userService: UserService,
    private _productService: ProductService,
    private _communitiesService: CommunitiesService,
    private _communityService: WebCommunityService,
    private _notificationService: NotificationService,
    private _avoService: AvoService,
    private _appService: AppService
  ) {}

  /**
   * The participation gate for a community, as replying uses it.
   *
   * Holds silently (`ok: false, reason: null`) while a product-gated community's product
   * list is still loading, so nobody gets told they don't own something they do own.
   */
  gateFor(communityStringId: string): ParticipationGate {
    const user = this._userService.user;
    const community = this._communitiesService.getCommunity(communityStringId);

    if (user && community?.participation_condition === "product_owner" && !this._productService.hasGottenProducts) {
      if (!this._productService.isGettingProducts) this._productService.getProducts();
      return { ok: false, reason: null };
    }

    return computeParticipationGate({
      community,
      user,
      profilePublic: !!this._communityService.profile?.public,
      subDetails: this._userService.subDetails,
      userGroups: Array.isArray(user?.groups) ? user.groups.map((g: any) => (typeof g === "string" ? g : g?.name)).filter(Boolean) : undefined,
      ownsProduct: (req: ProductRequirement) => this._productService.ownsProductRequirement(req),
    });
  }

  /**
   * Whether a reaction is allowed. Two deliberate differences from the reply gate, both
   * inherited from `comment.component.updateReactionGate`:
   *   - `profile_not_public` does NOT block: a reaction publishes no content the way a
   *     reply does, and blocking would newly lock out members who can react today.
   *   - a null reason is the products-still-loading hold, which fails open.
   * Beta membership is its own gate, so beta short-circuits to allowed.
   */
  canReact(communityStringId: string, betaStringId?: string): boolean {
    if (betaStringId) return true;
    const gate = this.gateFor(communityStringId);
    return gate.ok || gate.reason === "profile_not_public" || gate.reason === null;
  }

  /** Says why a reaction was refused, and routes to the fix where there is one. */
  explainBlock(gate: ParticipationGate, communityStringId: string) {
    switch (gate.reason) {
      case "needs_fsp_plus":
        this._router.navigate(["/onboarding"], { queryParams: { subscribeOnly: true } });
        break;
      case "needs_fender_play":
        window.open(environment.fenderPlayUrl, "_blank");
        break;
      case "needs_product_ownership": {
        const community = this._communitiesService.getCommunity(communityStringId);
        const names = (community?.requiredProducts || []).map((p) => p.label || p.value).join(", ") || "the required product";
        this._appService.alert(`Register one of the following products with your account to react here: ${names}.`);
        break;
      }
      case "needs_group_membership":
        this._appService.alert("Only members of specific groups can react in this community. Contact a community admin if you believe you should have access.");
        break;
      default:
        break;
    }
  }

  /**
   * Write the reaction, notify the author, track the event.
   *
   * Notification only fires when a reaction is being ADDED (not removed) and the reactor
   * isn't the author. It rides `community_post_liked` for every reaction type — the gate
   * on the recipient's side is `reactions_on_my_posts` (DENG-845).
   */
  persist(args: CommunityReactionPersistArgs) {
    const { post, selectedType, previousTypes, nextTypes, communityStringId, betaStringId, originalPost, channelName } = args;
    const isAdding = nextTypes.includes(selectedType) && !previousTypes.includes(selectedType);
    const me = this._userService.user;

    if (isAdding && me?.id != post.author?.id) {
      const notification = this._notificationService.createCommunityPostNotification("community_post_liked", me, post, undefined, originalPost, selectedType, communityStringId, betaStringId);
      this._notificationService
        .sendNotification({
          notification,
          user_ids: [post.author.id],
          clients: ["rollup", "mobile", "email"],
        })
        .subscribe();
    }

    this._communityService.setReaction(post, nextTypes, betaStringId ? `beta_community_posts` : environment.communityFirestoreCollection);

    if (isAdding) {
      const page = this._avoService.getPageProperties({ pageName: "Community" });
      const extra = {
        name: "Community",
        channelName: channelName,
        eventType: "post",
        postId: parseInt(post.id, 10) || undefined,
        interactionType: "liked",
        reactionType: selectedType,
      };
      this._avoService.trackEvent().communityViewed(this._avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true }));
    }
  }

  /**
   * Put a post's reaction map back the way it was after a blocked write. The reaction
   * control applies changes optimistically, so a refusal has to undo them.
   */
  revert(post: any, previousTypes: ReactionType[]) {
    const userId = this._userService.user?.id;
    if (!userId || !post) return;
    const reverted = { ...(post.reactions || {}) };
    if (previousTypes.length) reverted[userId] = previousTypes;
    else delete reverted[userId];
    post.reactions = reverted;
  }
}
